import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES } from './nav'

/**
 * The administration workspace's route tree (frontend phase-12 §3.1).
 *
 * Every route carries `workspace: 'admin'` and the single `admin.access` capability:
 * administration is one boundary, and the server enforces it again on every request. The
 * commission console (Phase 10) and platform analytics (Phase 11) keep their own route files;
 * this one adds the rest and the home page, and `router/workspaces.ts` marks the workspace as
 * owning its routes so the Phase 02 placeholder is no longer generated.
 */
const admin = {
  layout: 'dashboard',
  requiresAuth: true,
  workspace: 'admin' as const,
  capability: CAPABILITIES.adminAccess,
}

/**
 * The support officers' two pages, one lazy chunk (`pages/supportOfficers.ts`), reached through
 * a small loader so the entry carries none of their preload list.
 */
const supportOfficerPages = () =>
  import('./pages/supportOfficers.load').then((m) => m.loadSupportOfficerPages())

/** Roommate requests and affiliate institutions (2026-10-22): one lazy chunk, the same way. */
const partnerPages = () => import('./pages/partners.load').then((m) => m.loadPartnerPages())

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/app/admin',
    name: 'admin-overview',
    component: () => import('./pages/AdminOverviewPage.vue'),
    meta: { ...admin, title: 'admin.nav.overview' },
  },
  // Before `/affiliates/:id`, so "new" is never read as an id.
  {
    path: '/app/admin/affiliates/new',
    name: 'admin-affiliate-new',
    component: () => import('./pages/AdminAffiliateCreatePage.vue'),
    meta: { ...admin, title: 'admin.nav.affiliates' },
  },
  {
    path: '/app/admin/affiliates',
    name: 'admin-affiliates',
    component: () => import('./pages/AdminAffiliatesPage.vue'),
    meta: { ...admin, title: 'admin.nav.affiliates' },
  },
  {
    path: '/app/admin/affiliates/:id',
    name: 'admin-affiliate',
    component: () => import('./pages/AdminAffiliateDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.affiliates' },
  },
  {
    path: '/app/admin/landlords',
    name: 'admin-landlords',
    component: () => import('./pages/AdminLandlordsPage.vue'),
    meta: { ...admin, title: 'admin.nav.landlords' },
  },
  {
    path: '/app/admin/landlords/:id',
    name: 'admin-landlord',
    component: () => import('./pages/AdminLandlordDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.landlords' },
  },
  {
    path: '/app/admin/properties',
    name: 'admin-properties',
    component: () => import('./pages/AdminPropertiesPage.vue'),
    meta: { ...admin, title: 'admin.nav.properties' },
  },
  {
    path: '/app/admin/properties/:id',
    name: 'admin-property',
    component: () => import('./pages/AdminPropertyDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.properties' },
  },
  {
    path: '/app/admin/payments',
    name: 'admin-payments',
    component: () => import('./pages/AdminPaymentsPage.vue'),
    meta: { ...admin, title: 'admin.nav.payments' },
  },
  {
    path: '/app/admin/payments/:id',
    name: 'admin-payment',
    component: () => import('./pages/AdminPaymentDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.payments' },
  },
  // Students' space requests (2026-10-09): the queue the UNIZIK admission link feeds.
  {
    path: '/app/admin/space-requests',
    name: 'admin-space-requests',
    component: () => import('./pages/AdminSpaceRequestsPage.vue'),
    meta: { ...admin, title: 'admin.nav.spaceRequests' },
  },
  // Roommate requests (2026-10-22): a second register beside the space requests. Before
  // `/space-requests/:id`, so "roommates" is never read as a request's id.
  {
    path: '/app/admin/space-requests/roommates',
    name: 'admin-roommate-requests',
    component: () => partnerPages().then((m) => m.AdminRoommateRequestsPage),
    meta: { ...admin, title: 'admin.nav.spaceRequests' },
  },
  {
    path: '/app/admin/space-requests/roommates/:id',
    name: 'admin-roommate-request',
    component: () => partnerPages().then((m) => m.AdminRoommateRequestDetailPage),
    meta: { ...admin, title: 'admin.nav.spaceRequests' },
  },
  {
    path: '/app/admin/space-requests/:id',
    name: 'admin-space-request',
    component: () => import('./pages/AdminSpaceRequestDetailPage.vue'),
    meta: { ...admin, title: 'admin.nav.spaceRequests' },
  },
  // Affiliate institutions (2026-10-22). Before `/institutions/:id`, so "new" is never an id.
  {
    path: '/app/admin/institutions',
    name: 'admin-institutions',
    component: () => partnerPages().then((m) => m.AdminInstitutionsPage),
    meta: { ...admin, title: 'admin.nav.institutions' },
  },
  {
    path: '/app/admin/institutions/new',
    name: 'admin-institution-new',
    component: () => partnerPages().then((m) => m.AdminInstitutionCreatePage),
    meta: { ...admin, title: 'admin.nav.institutions' },
  },
  {
    path: '/app/admin/institutions/:id',
    name: 'admin-institution',
    component: () => partnerPages().then((m) => m.AdminInstitutionDetailPage),
    meta: { ...admin, title: 'admin.nav.institutions' },
  },
  {
    path: '/app/admin/audit',
    name: 'admin-audit',
    component: () => import('./pages/AdminAuditPage.vue'),
    meta: { ...admin, title: 'admin.nav.audit' },
  },
  {
    path: '/app/admin/integrity',
    name: 'admin-integrity',
    component: () => import('./pages/AdminIntegrityPage.vue'),
    meta: { ...admin, title: 'admin.nav.integrity' },
  },
  {
    path: '/app/admin/anomalies',
    name: 'admin-anomalies',
    component: () => import('./pages/AdminAnomaliesPage.vue'),
    meta: { ...admin, title: 'admin.nav.anomalies' },
  },
  {
    path: '/app/admin/system',
    name: 'admin-system',
    component: () => import('./pages/AdminSystemPage.vue'),
    meta: { ...admin, title: 'admin.nav.system' },
  },
  {
    path: '/app/admin/settings',
    name: 'admin-settings',
    component: () => import('./pages/AdminSettingsPage.vue'),
    meta: { ...admin, title: 'admin.nav.settings' },
  },
  {
    // APMP on WhatsApp (2026-10-12): the setup steps and the conversations, `?tab=` / `?step=`.
    path: '/app/admin/whatsapp',
    name: 'admin-whatsapp',
    component: () => import('./pages/AdminWhatsAppPage.vue'),
    meta: { ...admin, title: 'admin.nav.whatsapp' },
  },
  // APMP's support officers (2026-10-12, requesters-support-whatsapp.md §6): the register, with
  // "Register a support officer" in a slide-over, and one officer.
  {
    path: '/app/admin/support-officers',
    name: 'admin-support-officers',
    component: () => supportOfficerPages().then((m) => m.AdminSupportOfficersPage),
    meta: { ...admin, title: 'admin.nav.supportOfficers' },
  },
  {
    path: '/app/admin/support-officers/:id',
    name: 'admin-support-officer',
    component: () => supportOfficerPages().then((m) => m.AdminSupportOfficerDetailPage),
    meta: { ...admin, title: 'admin.nav.supportOfficers' },
  },
  {
    // The administrator's own account — the page every workspace has (2026-09-27). The title
    // key is the other workspaces' "My profile", so the three breadcrumbs read alike.
    path: '/app/admin/profile',
    name: 'admin-profile',
    component: () => import('./pages/AdminProfilePage.vue'),
    meta: { ...admin, title: 'landlord.nav.profile' },
  },
]
